import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useModalAccessibility } from "../../hooks/useModalAccessibility";
import { getCertificationKind } from "../../features/certifications/certificationKinds";
import styles from "./CertifiedCelebration.module.css";

// Fenetre de felicitations apres acceptation du statut « Organisateur certifie ».
// Confettis : uniquement transform + opacity, animes par le compositeur (GPU),
// sans layout ni repaint par image ; supprimes si l'utilisateur reduit les animations.

const CONFETTI_COUNT = 160;
const CONFETTI_COLORS = ["#b48cff", "#7a45f0", "#f6b73c", "#ffe29a", "#ffffff", "#ff7ac6"];

function buildConfetti() {
  return Array.from({ length: CONFETTI_COUNT }, (_, index) => {
    const angle = Math.random() * Math.PI * 2;
    const burst = 120 + Math.random() * 260;
    return {
      id: index,
      style: {
        "--bx": `${Math.cos(angle) * burst}px`,
        "--by": `${Math.sin(angle) * burst - 140}px`,
        "--ex": `${Math.cos(angle) * burst * 1.6 + (Math.random() - 0.5) * 200}px`,
        "--ey": `${65 + Math.random() * 45}vh`,
        "--r": `${(Math.random() - 0.5) * 1440}deg`,
        "--d": `${2.6 + Math.random() * 1.8}s`,
        "--delay": `${Math.random() * 0.25}s`,
        "--c": CONFETTI_COLORS[index % CONFETTI_COLORS.length],
        "--w": `${6 + Math.random() * 6}px`,
        "--h": `${8 + Math.random() * 10}px`,
      },
      round: index % 5 === 0,
    };
  });
}

// homeHref : bouton d'incitation vers l'accueil (apres acceptation) ; absent quand la
// fenetre est rejouee depuis le badge du profil.
// kind : ORGANIZER ou DRIVER (logo et textes du badge, cf. certificationKinds).
export default function CertifiedCelebration({ displayName = "", homeHref = null, kind = "ORGANIZER", onClose }) {
  const name = String(displayName || "").trim();
  const certification = getCertificationKind(kind);
  const confetti = useMemo(buildConfetti, []);
  const dialogRef = useRef(null);
  // Focus confine dans la fenetre et rendu au declencheur a la fermeture.
  useModalAccessibility(dialogRef);

  useEffect(() => {
    const handleKey = (event) => { if (event.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return createPortal(
    <div ref={dialogRef} className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="certified-celebration-title" tabIndex={-1}>
      <div className={styles.confettiLayer} aria-hidden="true">
        {confetti.map((piece) => (
          <span key={piece.id} className={`${styles.piece} ${piece.round ? styles.round : ""}`} style={piece.style} />
        ))}
      </div>
      <div className={styles.modal}>
        <div className={styles.badgeStage}>
          <span className={styles.halo} aria-hidden="true" />
          <img className={styles.badge} src={certification.logo} alt="" width="180" height="180" />
        </div>
        <h2 id="certified-celebration-title" className={styles.title}>
          {name ? `Bravo ${name} !` : "Félicitations !"}
        </h2>
        <p className={styles.text}>
          Tu fais maintenant partie des <strong>{certification.celebration.groupLabel}</strong> KARANGO.{" "}
          {certification.celebration.text}
        </p>
        <p className={styles.cheer}>{certification.celebration.cheer}</p>
        {homeHref && <a className={styles.primaryButton} href={homeHref}>{certification.celebration.cta}</a>}
        <button data-autofocus type="button" className={homeHref ? styles.secondaryButton : styles.primaryButton} onClick={onClose}>
          {homeHref ? "Plus tard" : "Fermer"}
        </button>
      </div>
    </div>,
    document.body
  );
}
